import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Radio, Spin, Tooltip } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import { usePaymentCalendarQuery } from 'core';
import { formatMoneyCurrency } from 'core/utils/format';
import { PATHS } from 'router/config';

import { buildWeeklyModel, IWeekBucket } from './weeklyModel';

export interface IWeeklyLiquidityChartProps {
  /**
   * Compact mode (FinanceDashboard): fixed 8 + 8 week horizon, no controls,
   * shorter body, whole card clicks through to the payment calendar page.
   */
  compact?: boolean;
}

const DATE_FMT = 'YYYY-MM-DD';

/* Palette (mirrors the calendar TONE vocabulary; kept local because organisms
 * must not import page-level modules). */
const COLOR = {
  ok: 'var(--k-fg-16a34a)',
  danger: 'var(--k-fg-ef4444)',
  dangerSoft: 'var(--k-bg-ef4444-a100)',
  muted: 'var(--k-fg-94a3b8)',
  ink: 'var(--k-fg-0f172a)',
  grid: 'var(--k-border-e2e8f0)',
};

const fmt = (n: number): string => formatMoneyCurrency(n, 'PLN');

/* ----- SVG geometry ----- */
const SLOT_W = 56;
const PAD_L = 8;
const PAD_R = 8;
const PLOT_TOP = 10;
const LABEL_H = 18;

interface IGeom {
  width: number;
  height: number;
  plotH: number;
  y: (v: number) => number;
}

const buildGeom = (weeks: IWeekBucket[], height: number): IGeom => {
  const width = PAD_L + weeks.length * SLOT_W + PAD_R;
  const plotH = height - PLOT_TOP - LABEL_H;
  const values = weeks.flatMap((w) => [
    w.available,
    w.expense,
    w.end,
    w.opening,
  ]);
  const rawMax = Math.max(0, ...values);
  const rawMin = Math.min(0, ...values);
  const span = rawMax - rawMin || 1;
  const max = rawMax + span * 0.06;
  const min = rawMin - (rawMin < 0 ? span * 0.06 : 0);
  const y = (v: number): number =>
    PLOT_TOP + ((max - v) / (max - min || 1)) * plotH;
  return { width, height, plotH, y };
};

/**
 * «Прогноз ликвидности по неделям» — an own-query SVG organism (no chart
 * libraries): per ISO week a stacked «Располагаем» bar (остаток на начало +
 * поступления) next to a red «Расходы» bar, an end-of-week balance line, a
 * «сегодня» marker, dimmed past weeks and red-flagged shortfall weeks. Full
 * mode exposes back 4/8 and forward 4/8/12 presets (default 8 + 8).
 */
export const WeeklyLiquidityChart = memo<IWeeklyLiquidityChartProps>(
  ({ compact }) => {
    const { t } = useTranslation();
    const history = useHistory();

    const [back, setBack] = useState<4 | 8>(8);
    const [fwd, setFwd] = useState<4 | 8 | 12>(8);
    const weeksBack = compact ? 8 : back;
    const weeksFwd = compact ? 8 : fwd;

    const range = useMemo(
      () => ({
        from: moment()
          .startOf('isoWeek')
          .subtract(weeksBack, 'week')
          .format(DATE_FMT),
        to: moment()
          .startOf('isoWeek')
          .add(weeksFwd, 'week')
          .add(6, 'day')
          .format(DATE_FMT),
      }),
      [weeksBack, weeksFwd],
    );

    const { data, loading } = usePaymentCalendarQuery({
      variables: { dateFrom: range.from, dateTo: range.to },
      fetchPolicy: 'cache-and-network',
    });

    const weeks = useMemo(
      () => buildWeeklyModel(data?.paymentCalendar?.days || []),
      [data],
    );

    const height = compact ? 150 : 220;
    const geom = useMemo(() => buildGeom(weeks, height), [weeks, height]);

    const todayIso = moment().format(DATE_FMT);
    const skipLabels = weeks.length > 12 ? 2 : 1;

    const linePoints = weeks
      .map(
        (w, i) =>
          `${PAD_L + i * SLOT_W + SLOT_W / 2},${geom.y(w.end).toFixed(1)}`,
      )
      .join(' ');

    const body =
      loading && weeks.length === 0 ? (
        <Loader>
          <Spin size="small" />
        </Loader>
      ) : (
        <SvgScroll>
          <svg
            viewBox={`0 0 ${geom.width} ${geom.height}`}
            width="100%"
            height={height}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={t('pages.paymentCalendar.weeklyChart.title')}
          >
            {/* zero line */}
            <line
              x1={PAD_L}
              x2={geom.width - PAD_R}
              y1={geom.y(0)}
              y2={geom.y(0)}
              stroke={COLOR.grid}
              strokeWidth={1}
            />

            {weeks.map((w, i) => {
              const x0 = PAD_L + i * SLOT_W;
              const isPast = w.weekEnd < todayIso;
              const barW = 16;
              const gap = 6;
              const availX = x0 + SLOT_W / 2 - barW - gap / 2;
              const expX = x0 + SLOT_W / 2 + gap / 2;
              const zeroY = geom.y(0);

              // «Располагаем»: opening segment + income segment stacked.
              const openingTop = geom.y(Math.max(w.opening, 0));
              const openingBottom = w.opening >= 0 ? zeroY : geom.y(w.opening);
              const availTop = geom.y(Math.max(w.available, 0));

              return (
                <g key={w.weekStart} opacity={isPast ? 0.45 : 1}>
                  {w.shortfall && (
                    <rect
                      x={x0 + 2}
                      y={PLOT_TOP - 4}
                      width={SLOT_W - 4}
                      height={geom.plotH + 8}
                      fill={COLOR.dangerSoft}
                      stroke={COLOR.danger}
                      strokeDasharray="3 3"
                      strokeWidth={1}
                      rx={4}
                    />
                  )}

                  {/* opening part */}
                  {w.opening !== 0 && (
                    <rect
                      x={availX}
                      y={w.opening >= 0 ? openingTop : zeroY}
                      width={barW}
                      height={Math.max(
                        Math.abs(openingBottom - openingTop) -
                          (w.opening >= 0 ? 0 : 0),
                        0.5,
                      )}
                      fill={w.opening >= 0 ? COLOR.muted : COLOR.danger}
                      opacity={w.opening >= 0 ? 0.8 : 0.5}
                      rx={2}
                    />
                  )}
                  {/* income part (from opening level up to available) */}
                  {w.income > 0 && (
                    <rect
                      x={availX}
                      y={availTop}
                      width={barW}
                      height={Math.max(
                        geom.y(Math.max(w.opening, 0)) - availTop,
                        0.5,
                      )}
                      fill={COLOR.ok}
                      rx={2}
                    />
                  )}
                  {/* expenses bar */}
                  {w.expense > 0 && (
                    <rect
                      x={expX}
                      y={geom.y(w.expense)}
                      width={barW}
                      height={Math.max(zeroY - geom.y(w.expense), 0.5)}
                      fill={COLOR.danger}
                      rx={2}
                    />
                  )}

                  {/* week label */}
                  {i % skipLabels === 0 && (
                    <text
                      x={x0 + SLOT_W / 2}
                      y={geom.height - 5}
                      textAnchor="middle"
                      fontSize={8.5}
                      fill={COLOR.muted}
                    >
                      {moment(w.weekStart).format('DD.MM')}–
                      {moment(w.weekEnd).format('DD.MM')}
                    </text>
                  )}

                  {/* hover target + tooltip */}
                  <Tooltip
                    title={
                      <TooltipBody>
                        <div className="wlc-caption">
                          {moment(w.weekStart).format('DD.MM')}–
                          {moment(w.weekEnd).format('DD.MM')}
                        </div>
                        <div>
                          {t(
                            'pages.paymentCalendar.weeklyChart.tooltipOpening',
                          )}
                          : {fmt(w.opening)}
                        </div>
                        <div>
                          +{' '}
                          {t('pages.paymentCalendar.weeklyChart.tooltipIncome')}
                          : {fmt(w.income)}
                        </div>
                        <div>
                          ={' '}
                          {t(
                            'pages.paymentCalendar.weeklyChart.tooltipAvailable',
                          )}
                          : {fmt(w.available)}
                        </div>
                        <div>
                          −{' '}
                          {t(
                            'pages.paymentCalendar.weeklyChart.tooltipExpenses',
                          )}
                          : {fmt(w.expense)}
                        </div>
                        <div>
                          = {t('pages.paymentCalendar.weeklyChart.tooltipEnd')}:{' '}
                          {fmt(w.end)}
                        </div>
                        {w.shortfall && (
                          <div className="wlc-shortfall">
                            {t('pages.paymentCalendar.weeklyChart.shortfall')} ·{' '}
                            {fmt(w.minBalance)}
                          </div>
                        )}
                      </TooltipBody>
                    }
                  >
                    <rect
                      x={x0}
                      y={0}
                      width={SLOT_W}
                      height={geom.height - LABEL_H}
                      fill="transparent"
                    />
                  </Tooltip>
                </g>
              );
            })}

            {/* end-of-week balance line + points */}
            {weeks.length > 0 && (
              <>
                <polyline
                  points={linePoints}
                  fill="none"
                  stroke={COLOR.ink}
                  strokeWidth={1.5}
                />
                {weeks.map((w, i) => (
                  <circle
                    key={`pt-${w.weekStart}`}
                    cx={PAD_L + i * SLOT_W + SLOT_W / 2}
                    cy={geom.y(w.end)}
                    r={2.5}
                    fill={w.end < 0 ? COLOR.danger : COLOR.ink}
                  />
                ))}
              </>
            )}

            {/* today marker */}
            {weeks.map((w, i) => {
              if (todayIso < w.weekStart || todayIso > w.weekEnd) return null;
              const frac = (moment(todayIso).isoWeekday() - 0.5) / 7; // mid-day position
              const x = PAD_L + i * SLOT_W + SLOT_W * frac;
              return (
                <g key="today">
                  <line
                    x1={x}
                    x2={x}
                    y1={PLOT_TOP - 6}
                    y2={geom.height - LABEL_H}
                    stroke={COLOR.ink}
                    strokeWidth={1}
                    strokeDasharray="4 3"
                  />
                  <text
                    x={x + 3}
                    y={PLOT_TOP + 2}
                    fontSize={8.5}
                    fill={COLOR.ink}
                    fontWeight={700}
                  >
                    {t('pages.paymentCalendar.weeklyChart.today')}
                  </text>
                </g>
              );
            })}
          </svg>
        </SvgScroll>
      );

    const legend = (
      <Legend>
        <span>
          <i style={{ background: COLOR.muted }} />
          {t('pages.paymentCalendar.weeklyChart.openingPart')}
        </span>
        <span>
          <i style={{ background: COLOR.ok }} />
          {t('pages.paymentCalendar.weeklyChart.incomePart')}
        </span>
        <span>
          <i style={{ background: COLOR.danger }} />
          {t('pages.paymentCalendar.weeklyChart.expenses')}
        </span>
        <span>
          <i style={{ background: COLOR.ink, borderRadius: '50%' }} />
          {t('pages.paymentCalendar.weeklyChart.endBalance')}
        </span>
      </Legend>
    );

    if (compact) {
      return (
        <CompactCard
          type="button"
          onClick={() => history.push(PATHS.financesPaymentCalendar)}
        >
          <Header>
            <span className="wlc-title">
              {t('pages.paymentCalendar.weeklyChart.title')}
            </span>
          </Header>
          {body}
        </CompactCard>
      );
    }

    return (
      <FullCard>
        <Header>
          <span className="wlc-title">
            {t('pages.paymentCalendar.weeklyChart.title')}
          </span>
          <Controls>
            <Radio.Group
              size="small"
              value={back}
              onChange={(e) => setBack(e.target.value as 4 | 8)}
            >
              <Radio.Button value={4}>
                {t('pages.paymentCalendar.weeklyChart.back4')}
              </Radio.Button>
              <Radio.Button value={8}>
                {t('pages.paymentCalendar.weeklyChart.back8')}
              </Radio.Button>
            </Radio.Group>
            <Radio.Group
              size="small"
              value={fwd}
              onChange={(e) => setFwd(e.target.value as 4 | 8 | 12)}
            >
              <Radio.Button value={4}>
                {t('pages.paymentCalendar.weeklyChart.fwd4')}
              </Radio.Button>
              <Radio.Button value={8}>
                {t('pages.paymentCalendar.weeklyChart.fwd8')}
              </Radio.Button>
              <Radio.Button value={12}>
                {t('pages.paymentCalendar.weeklyChart.fwd12')}
              </Radio.Button>
            </Radio.Group>
          </Controls>
        </Header>
        {body}
        {legend}
      </FullCard>
    );
  },
);

/* ----------------------------- styles ----------------------------- */

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;

  .wlc-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--k-fg-0f172a);
  }
`;

const Controls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  /* keep the checked segment from stacking above nav overlays */
  .ant-radio-button-wrapper,
  .ant-radio-button-wrapper:hover,
  .ant-radio-button-wrapper-checked {
    z-index: auto;
  }
`;

const FullCard = styled.div`
  background: var(--k-bg-ffffff);
`;

const CompactCard = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:hover {
    border-color: var(--k-border-cbd5f5);
    box-shadow: 0 1px 4px var(--k-shadow-0f172a-a60);
  }
`;

const SvgScroll = styled.div`
  overflow-x: auto;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-size: 11px;
  color: var(--k-fg-64748b);

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }
`;

const TooltipBody = styled.div`
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  .wlc-caption {
    font-weight: 700;
    margin-bottom: 4px;
  }
  .wlc-shortfall {
    margin-top: 4px;
    font-weight: 700;
    color: var(--k-fg-fecaca);
  }
`;
